import { useQuery, useMutation } from '@apollo/client'
import gql from 'graphql-tag'
import * as React from 'react'

import { GetConversation, GetConversationParticipantsButAuthor } from './TalkApollo.queries'
import {
	AddParticipantsToConversation,
	AddUsersToConversationUnreadMessages,
	AddUserToCommentBookmarkedByList,
	AddUserToConversationBookmarkedByList,
	AddUserToDislikedByList,
	AddUserToLikedByList,
	CreateComment,
	RemoveUserFromCommentBookmarkedByList,
	RemoveUserFromConversationBookmarkedByList,
	RemoveUserFromDislikedByList,
	RemoveUserFromLikedByList,
	SetConversationAsRead,
	UpdateComment
} from './TalkApollo.mutations'

import { OptionsProps, Talk, TalkProps } from './Talk'
import { Comment, Conversation, User } from './dtos'
import { TalkApolloClient } from '../talk-apollo-client'

import { ErrorBoundary } from '../ErrorBoundary'

export interface TalkApolloProps {
	client: TalkApolloClient
	conversationId: string
	headless?: boolean
	onGoToConversationsClick?: () => void
	userId: string
	options?: OptionsProps
	talkRef: (ref: React.MutableRefObject<any>) => void
	onLinkClick?: (url: string) => void
	getTributeComponent: any
	pollInterval?: number
	tributes?: Array<any>
}

export const TalkApollo: React.FC<TalkApolloProps> = (props) => {
	return (
		<TalkApolloContainer
			client={props.client}
			conversationId={props.conversationId}
			headless={props.headless}
			userId={props.userId}
			onGoToConversationsClick={props.onGoToConversationsClick}
			options={props.options}
			talkRef={props.talkRef}
			onLinkClick={props.onLinkClick}
			getTributeComponent={props.getTributeComponent}
			pollInterval={props.pollInterval}
			tributes={props.tributes}
		/> 
	)
}

const TalkApolloContainer: React.FC<{
	client: TalkApolloClient
	conversationId: string
	headless?: boolean
	onGoToConversationsClick?: () => void
	userId: string
	options?: OptionsProps
	talkRef: (ref: React.MutableRefObject<any>) => void
	onLinkClick?: (url: string) => void
	getTributeComponent: any
	tributes?: Array<any>
	pollInterval?: number
}> = (props) => {
	const { client, getTributeComponent, talkRef, onLinkClick } = props
	const conversation: { id: string } = { id: props.conversationId }
	const pollInterval = props.pollInterval !== undefined ? props.pollInterval : 5000
	const {
		data: conversationData,
		error: conversationError,
		loading: conversationLoading
	} = useQuery(GetConversation, {
		client,
		variables: { conversation },
		pollInterval
	})

	const {
		data: participantsExceptCommentAuthorData,
		error: participantsError
	} = useQuery(GetConversationParticipantsButAuthor, {
		client,
		variables: {
			conversationId: props.conversationId,
			commentCreatorOauthId: props.userId
		}
	})

	const [ addUserToLikedByList ] = useMutation(AddUserToLikedByList, { client })
	const [ removeUserFromLikedByList ] = useMutation(RemoveUserFromLikedByList, { client })
	const [ addUsersToConversationUnreadMessages ] = useMutation(AddUsersToConversationUnreadMessages, { client })
	const [ setConversationAsRead ] = useMutation(SetConversationAsRead, { client })

	const [ addUserToCommentBookmarkedByList ] = useMutation(AddUserToCommentBookmarkedByList, { client })
	const [ removeUserFromCommentBookmarkedByList ] = useMutation(RemoveUserFromCommentBookmarkedByList, { client })

	const [ addUserToDislikedByList ] = useMutation(AddUserToDislikedByList, { client })
	const [ removeUserFromDislikedByList ] = useMutation(RemoveUserFromDislikedByList, { client })

	const [ addUserToConversationBookmarkedByList ] = useMutation(AddUserToConversationBookmarkedByList, { client })
	const [ removeUserToConversationBookmarkedByList ] = useMutation(RemoveUserFromConversationBookmarkedByList, {
		client
	})

	const [ addParticipantsToConversation ] = useMutation(AddParticipantsToConversation, { client })

	const [ addComment ] = useMutation(CreateComment, { client })
	const [ updateComment ] = useMutation(UpdateComment, { client })

	if (conversationLoading) return <div>loading...</div>

	if (conversationError) return <div>error...</div>

	const likeComment = (commentId: string) => {
        const talkData: Conversation = conversationData.conversation
        // @ts-ignore
        const comment: Comment = findComment(commentId, talkData.comments)
        // @ts-ignore
		const foundUser: User = comment.likedBy.find((u) => u.oauthId === props.userId)

		if (!foundUser) {
			addUserToLikedByList({
				variables: {
					commentId: commentId,
					oauthId: props.userId
				}
			})
			removeUserFromDislikedByList({
				variables: {
					commentId: commentId,
					oauthId: props.userId
				}
			})
		} else {
			removeUserFromLikedByList({
				variables: {
					commentId: commentId,
					oauthId: props.userId
				}
			})
		}
	}

	const bookmarkComment = (commentId: string) => {
        const talkData: Conversation = conversationData.conversation
        // @ts-ignore
		const comment: Comment = findComment(commentId, talkData.comments)
        // @ts-ignore
        const foundUser: User = comment.bookmarkedBy.find((u) => u.oauthId === props.userId)

		if (!foundUser) {
			addUserToCommentBookmarkedByList({
				variables: {
					commentId: commentId,
					oauthId: props.userId
				}
			})
		} else {
			removeUserFromCommentBookmarkedByList({
				variables: {
					commentId: commentId,
					oauthId: props.userId
				}
			})
		}
	}

	const dislikeComment = (commentId: string) => {
        const talkData: Conversation = conversationData.conversation
        // @ts-ignore
		const comment: Comment = findComment(commentId, talkData.comments)
        // @ts-ignore
        const foundUser: User = comment.dislikedBy.find((u) => u.oauthId === props.userId)

		if (!foundUser) {
			addUserToDislikedByList({
				variables: {
					commentId: commentId,
					oauthId: props.userId
				}
			})
			removeUserFromLikedByList({
				variables: {
					commentId: commentId,
					oauthId: props.userId
				}
			})
		} else {
			removeUserFromDislikedByList({
				variables: {
					commentId: commentId,
					oauthId: props.userId
				}
			})
		}
	}

	const bookmarkConversation = (conversationId: string) => {
		const foundUser: User = conversationData.conversation.bookmarkedBy.find((u: any) => u.oauthId === props.userId)

		if (!foundUser) {
			addUserToConversationBookmarkedByList({
				variables: {
					conversationId: conversationId,
					oauthId: props.userId
				}
			})
		} else {
			removeUserToConversationBookmarkedByList({
				variables: {
					conversationId: conversationId,
					oauthId: props.userId
				}
			})
		}
	}

	const findComment = (id: string, comments: Comment[]): Comment => {
		return comments.filter((c) => c.id === id)[0]
		// let foundComment: Comment = undefined
		// const loop = (comments: Comment[]) => {
		// 	comments &&
		// 		comments.map((comment) => {
		// 			if (comment.id === id) {
		// 				foundComment = comment
		// 			} else {
		// 				loop(comment.comments)
		// 			}
		// 		})
		// }

		// loop(comments)
		// return foundComment
	}

	const markConversationAsRead = (isLastMessageRead: boolean) => {
		isLastMessageRead &&
			setConversationAsRead({
				variables: {
					conversationId: props.conversationId,
					participantsOauthIds: [ { oauthId: props.userId } ]
				}
			})
	}

	const addParticipants = (conversationId: string, participantsOauthIds: { oauthId: string }[]) => {
		addParticipantsToConversation({
			variables: {
				conversationId,
				participantsOauthIds
			}
		})
	}

	const onAddComment = (comment: Comment) => {
		// HERE you should add comments property
		const data = {
			author: { connect: { oauthId: comment.author.id } },
			content: comment.content,
			parentConversation: {
				connect: comment.parentConversation
			},
			parentComment: {
				connect: comment.parentComment
			}
		}

		addComment({
			optimisticResponse: {
				__typename: 'Mutation',
				createComment: {
					__typename: 'Comment',
					// negative id
					id: -parseInt(Math.random().toString(8).substr(2, 9), 10),
					content: comment.content,
					author: { id: comment.author.id, __typename: 'User' },
					parentConversation: {
						id: comment.parentConversation,
						__typename: 'Conversation'
					},
					parentComment: undefined
				}
			},
			update: (proxy, result: any) => {
				const { conversation } = {
					...proxy.readQuery({
						query: GetConversation,
						variables: { conversation: comment.parentConversation }
					})
				}

				// // TODO: waiting for fix of https://github.com/apollographql/apollo-link-state/pull/235
				// proxy.writeFragment({
				// 	fragment: gql`
				// 		fragment myConversation on Conversation {
				// 			id
				// 			comments {
				// 				id
				// 			}
				// 		}
				// 	`,
				// 	id: `Conversation:${comment.parentConversation.id}`,
				// 	data: {
				// 		__typename: 'Conversation',
				// 		title: `${conversation.title} 2`,
				// 		comments: [ ...conversation.comments, result.data.createComment ].map((comment) => {
				// 			return { ...comment, __typename: 'Comment' }
				// 		})
				// 	}
				// })
			},
			refetchQueries: [
				{
					query: GetConversation,
					variables: {
						conversation: comment.parentConversation
					}
				}
			],
			variables: { data }
		})

		const participantsWhoDidNotReadComment = participantsExceptCommentAuthorData.conversation.participants.map(
			(item: any) => {
				return { oauthId: item['oauthId'] }
			}
		)

		addUsersToConversationUnreadMessages({
			variables: {
				conversationId: props.conversationId,
				participantsOauthIds: participantsWhoDidNotReadComment
			}
		})

		addParticipants(props.conversationId, [ { oauthId: comment.author.id } ])
	}

	const onEditComment = (comment: Comment) => {
		updateComment({
			variables: {
				content: comment.content,
				commentId: comment.id
			},
			optimisticResponse: {
				__typename: 'Mutation',
				updateComment: {
					__typename: 'Comment',
					id: comment.id,
					content: comment.content
				}
			},
			update: (proxy, { data }: any) => {
				proxy.writeFragment({
					id: comment.id,
					fragment: gql`
						fragment myComment on Comment {
							content
							__typename
						}
					`,
					data: {
						content: data.updateComment.content,
						__typename: 'Comment'
					}
				})
			}
		})
	}

	const talkProps: TalkProps = {
		addComment: onAddComment,
		conversation: conversationData.conversation,
		editComment: onEditComment,
		headless: props.headless,
		onGoToConversationsClick: props.onGoToConversationsClick,
		bookmarkConversation: bookmarkConversation,
		bookmarkComment: bookmarkComment,
		likeComment: likeComment,
		dislikeComment: dislikeComment,
		user: { id: props.userId },
		options: props.options,
		setConversationAsRead: markConversationAsRead,
		talkRef,
		onLinkClick,
		getTributeComponent,
		tributes: props.tributes
	}

	return talkProps.conversation === undefined ? (
		<div>loading conversation...</div>
	) : (
		<ErrorBoundary>
			<Talk {...talkProps} />
		</ErrorBoundary>
	)
}
